import { useState, useCallback } from 'react'
import type { Page, Block, BlockType } from '../types'

function uid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 9)
}

function createBlock(type: BlockType = 'text', content = ''): Block {
  return { id: uid(), type, content }
}

const SECTION_DEFAULT_TITLES: Record<string, string> = {
  'product-hub': 'New Feature',
  'persona-hub': 'New Persona',
  tests: 'New Test',
}

function createPage(parentSection: string = ''): Page {
  return {
    id: uid(),
    title: SECTION_DEFAULT_TITLES[parentSection] ?? 'Untitled',
    blocks: [createBlock()],
    createdAt: Date.now(),
    parentSection,
  }
}

export function usePages() {
  const [pages, setPages] = useState<Page[]>([])
  const [activePageId, setActivePageId] = useState<string | null>(null)

  const activePage = activePageId
    ? (pages.find((p) => p.id === activePageId) ?? null)
    : null

  const updatePage = useCallback(
    (pageId: string, updater: (page: Page) => Page) => {
      setPages((prev) => prev.map((p) => (p.id === pageId ? updater(p) : p)))
    },
    [],
  )

  const addPage = useCallback((parentSection: string) => {
    const page = createPage(parentSection)
    setPages((prev) => [...prev, page])
    setActivePageId(page.id)
    return page
  }, [])

  const deletePage = useCallback(
    (pageId: string) => {
      setPages((prev) => {
        const next = prev.filter((p) => p.id !== pageId)
        if (pageId === activePageId) {
          setActivePageId(null)
        }
        return next
      })
    },
    [activePageId],
  )

  const setActivePage = useCallback((pageId: string) => {
    setActivePageId(pageId)
  }, [])

  const clearActivePage = useCallback(() => {
    setActivePageId(null)
  }, [])

  const updatePageTitle = useCallback(
    (title: string) => {
      if (!activePage) return
      updatePage(activePage.id, (p) => ({ ...p, title }))
    },
    [activePage, updatePage],
  )

  const updatePageTitleById = useCallback(
    (pageId: string, title: string) => {
      updatePage(pageId, (p) => ({ ...p, title }))
    },
    [updatePage],
  )

  const addBlock = useCallback(
    (afterBlockId: string, type: BlockType = 'text', content = ''): Block => {
      const block = createBlock(type, content)
      if (!activePage) return block
      updatePage(activePage.id, (p) => {
        const idx = p.blocks.findIndex((b) => b.id === afterBlockId)
        const newBlocks = [...p.blocks]
        newBlocks.splice(idx + 1, 0, block)
        return { ...p, blocks: newBlocks }
      })
      return block
    },
    [activePage, updatePage],
  )

  const updateBlock = useCallback(
    (blockId: string, content: string) => {
      if (!activePage) return
      updatePage(activePage.id, (p) => ({
        ...p,
        blocks: p.blocks.map((b) => (b.id === blockId ? { ...b, content } : b)),
      }))
    },
    [activePage, updatePage],
  )

  const deleteBlock = useCallback(
    (blockId: string) => {
      if (!activePage) return
      updatePage(activePage.id, (p) => {
        const filtered = p.blocks.filter((b) => b.id !== blockId)
        if (filtered.length === 0) {
          return { ...p, blocks: [createBlock()] }
        }
        return { ...p, blocks: filtered }
      })
    },
    [activePage, updatePage],
  )

  const changeBlockType = useCallback(
    (blockId: string, type: BlockType) => {
      if (!activePage) return
      updatePage(activePage.id, (p) => ({
        ...p,
        blocks: p.blocks.map((b) => (b.id === blockId ? { ...b, type } : b)),
      }))
    },
    [activePage, updatePage],
  )

  return {
    pages,
    activePage,
    activePageId,
    addPage,
    deletePage,
    setActivePage,
    clearActivePage,
    updatePageTitle,
    updatePageTitleById,
    addBlock,
    updateBlock,
    deleteBlock,
    changeBlockType,
  }
}
